/**
 * SFrame150 — Debts & Liabilities (secure-inventory si-3)
 * Consolidated inventory page: Discovery checklist, Debts CRUD, Expenses CRUD.
 *
 * Persistence: persistSnapshot replaces the entire frame data object (server-side upsert).
 * All mutations pass a full SFrame150FrameData snapshot so sibling state is never wiped.
 *
 * Documents: per-item document upload using settlement document utilities.
 * Debt docs are tagged `debt:{item.id}`, expense docs are tagged `expense:{item.id}`.
 * Old task-scoped documents (from the previous accordion UI) remain accessible via the
 * step-level document section rendered by SettlementStepPage (settlementDocumentContext).
 *
 * Migration: existing task-based data (saved as { tasks: Record<TaskId, TaskSlice> }) is
 * converted on first load to the new { debts[], expenses[] } shape.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../ui/Modal';
import { SettlementStepPage } from '../../settlement';
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
  type SettlementDocumentRow,
} from '../../../utils/settlementDocuments';

const _step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-3')!;
const PHASE = 'secure-inventory' as const;
const STEP_ID = _step.id;
const FRAME_ID = 'sframe-150';

// Override title, description, and subTasks — suppresses old checklist UI and uses ticket copy
const step = {
  ..._step,
  title: 'Debts & Liabilities',
  description:
    'Identify, review, and manage outstanding debts, recurring expenses, and obligations that need attention during settlement.',
  subTasks: [],
};

// ── Types ─────────────────────────────────────────────────────────────────────

type DebtType = 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';
type ExpenseType =
  | 'credit_card'
  | 'utilities'
  | 'insurance'
  | 'subscriptions'
  | 'transportation'
  | 'healthcare'
  | 'other';
type ExecutorStatus = 'needs_review' | 'in_progress' | 'resolved';
type PaymentStatus = 'current' | 'past_due' | 'unknown';
type ActionRequired = 'pay' | 'continue' | 'cancel' | 'close' | 'review';

interface DebtItem {
  id: string;
  type: DebtType;
  creditorName: string;
  estimatedAmount: string;
  accountNumber: string;
  dueDate: string;
  providerCompany: string;
  notes: string;
  status: ExecutorStatus;
  paymentStatus: PaymentStatus;
  actionRequired: ActionRequired;
  documentIds: string[];
}

interface ExpenseItem {
  id: string;
  type: ExpenseType;
  expenseName: string;
  estimatedAmount: string;
  frequency: 'monthly' | 'quarterly' | 'annually' | 'unknown';
  accountNumber: string;
  dueDate: string;
  providerCompany: string;
  notes: string;
  status: ExecutorStatus;
  paymentStatus: PaymentStatus;
  actionRequired: ActionRequired;
  documentIds: string[];
}

interface SFrame150FrameData {
  discoveryChecklist: Record<string, boolean>;
  debts: DebtItem[];
  expenses: ExpenseItem[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DISCOVERY_ITEMS: { key: string; label: string }[] = [
  { key: 'credit_reports', label: 'Obtain credit reports (Equifax, Experian, TransUnion)' },
  { key: 'review_reports', label: 'Review reports for open accounts and balances' },
  { key: 'review_statements', label: 'Review bank and credit card statements for recurring charges' },
  { key: 'check_mail', label: 'Check mail and email for bills and notices' },
  { key: 'contact_creditors', label: 'Contact known creditors to confirm balances' },
  { key: 'tax_obligations', label: 'Check for outstanding tax obligations' },
  { key: 'medical_bills', label: 'Identify medical bills and personal debts' },
];

const DEBT_TILES: { type: DebtType; label: string }[] = [
  { type: 'credit_card', label: 'Credit Card Debt' },
  { type: 'personal_auto', label: 'Personal & Auto Loans' },
  { type: 'mortgage', label: 'Property Mortgages' },
  { type: 'medical', label: 'Medical Expenses' },
  { type: 'tax', label: 'Tax Debt' },
  { type: 'other', label: 'Other Loans' },
];

const EXPENSE_TILES: { type: ExpenseType; label: string }[] = [
  { type: 'credit_card', label: 'Credit Card Payments' },
  { type: 'utilities', label: 'Utility Bills' },
  { type: 'insurance', label: 'Insurance Premiums' },
  { type: 'subscriptions', label: 'Recurring Subscriptions' },
  { type: 'transportation', label: 'Transportation Expenses' },
  { type: 'healthcare', label: 'Healthcare Expenses' },
  { type: 'other', label: 'Other Expenses' },
];

const DEBT_TYPE_LABELS: Record<DebtType, string> = {
  credit_card: 'Credit Card Debt',
  personal_auto: 'Personal & Auto Loans',
  mortgage: 'Property Mortgages',
  medical: 'Medical Expenses',
  tax: 'Tax Debt',
  other: 'Other Loans',
};

const EXPENSE_TYPE_LABELS: Record<ExpenseType, string> = {
  credit_card: 'Credit Card Payments',
  utilities: 'Utility Bills',
  insurance: 'Insurance Premiums',
  subscriptions: 'Recurring Subscriptions',
  transportation: 'Transportation Expenses',
  healthcare: 'Healthcare Expenses',
  other: 'Other Expenses',
};

// ── Defaults ──────────────────────────────────────────────────────────────────

function defaultDiscovery(): Record<string, boolean> {
  return Object.fromEntries(DISCOVERY_ITEMS.map((i) => [i.key, false]));
}

function defaultFrameData(): SFrame150FrameData {
  return { discoveryChecklist: defaultDiscovery(), debts: [], expenses: [] };
}

function defaultDebt(type: DebtType): DebtItem {
  return {
    id: crypto.randomUUID(),
    type,
    creditorName: '',
    estimatedAmount: '',
    accountNumber: '',
    dueDate: '',
    providerCompany: '',
    notes: '',
    status: 'needs_review',
    paymentStatus: 'unknown',
    actionRequired: 'review',
    documentIds: [],
  };
}

function defaultExpense(type: ExpenseType): ExpenseItem {
  return {
    id: crypto.randomUUID(),
    type,
    expenseName: '',
    estimatedAmount: '',
    frequency: 'monthly',
    accountNumber: '',
    dueDate: '',
    providerCompany: '',
    notes: '',
    status: 'needs_review',
    paymentStatus: 'unknown',
    actionRequired: 'review',
    documentIds: [],
  };
}

// ── Migration from legacy task-based data ─────────────────────────────────────

function uid() {
  return `id-${Math.random().toString(36).slice(2, 11)}`;
}

interface LegacySecuredRow {
  id?: string;
  creditor: string;
  accountId: string;
  balance: string;
  notes: string;
}
interface LegacyCCRow {
  id?: string;
  creditor: string;
  accountId: string;
  balance: string;
  notes: string;
}
interface LegacyMedRow {
  id?: string;
  provider: string;
  serviceDate: string;
  serviceDetails: string;
  balance: string;
  status: string;
  notes: string;
}
interface LegacyPSRow {
  id?: string;
  creditor: string;
  account: string;
  balance: string;
  notes: string;
}
interface LegacyTaxRow {
  id?: string;
  agency: string;
  caseRef: string;
  balance: string;
  notes: string;
}
interface LegacyTaskSlice {
  creditBureaus?: { equifax?: boolean; experian?: boolean; transunion?: boolean };
  mortgageRows?: LegacySecuredRow[];
  vehicleRows?: LegacySecuredRow[];
  creditCardRows?: LegacyCCRow[];
  medicalRows?: LegacyMedRow[];
  personalStudentRows?: LegacyPSRow[];
  taxRows?: LegacyTaxRow[];
}

function migrateLegacyTasks(tasks: Record<string, unknown>): SFrame150FrameData {
  const debts: DebtItem[] = [];
  const discoveryChecklist = defaultDiscovery();

  // credit-reports task → discovery checklist
  const crTask = tasks['credit-reports'] as LegacyTaskSlice | undefined;
  if (crTask?.creditBureaus) {
    const { equifax, experian, transunion } = crTask.creditBureaus;
    if (equifax || experian || transunion) discoveryChecklist['credit_reports'] = true;
  }

  // mortgages
  const mortTask = tasks['mortgages'] as LegacyTaskSlice | undefined;
  for (const r of mortTask?.mortgageRows ?? []) {
    if (!r.creditor?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('mortgage'),
      id: r.id || uid(),
      creditorName: r.creditor || '',
      estimatedAmount: r.balance || '',
      accountNumber: r.accountId || '',
      notes: r.notes || '',
    });
  }

  // vehicle-loans
  const vehTask = tasks['vehicle-loans'] as LegacyTaskSlice | undefined;
  for (const r of vehTask?.vehicleRows ?? []) {
    if (!r.creditor?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('personal_auto'),
      id: r.id || uid(),
      creditorName: r.creditor || '',
      estimatedAmount: r.balance || '',
      accountNumber: r.accountId || '',
      notes: r.notes || '',
    });
  }

  // credit-cards
  const ccTask = tasks['credit-cards'] as LegacyTaskSlice | undefined;
  for (const r of ccTask?.creditCardRows ?? []) {
    if (!r.creditor?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('credit_card'),
      id: r.id || uid(),
      creditorName: r.creditor || '',
      estimatedAmount: r.balance || '',
      accountNumber: r.accountId || '',
      notes: r.notes || '',
    });
  }

  // medical
  const medTask = tasks['medical'] as LegacyTaskSlice | undefined;
  for (const r of medTask?.medicalRows ?? []) {
    if (!r.provider?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('medical'),
      id: r.id || uid(),
      creditorName: r.provider || '',
      estimatedAmount: r.balance || '',
      notes: [r.serviceDetails, r.notes].filter(Boolean).join(' — '),
    });
  }

  // personal-student
  const psTask = tasks['personal-student'] as LegacyTaskSlice | undefined;
  for (const r of psTask?.personalStudentRows ?? []) {
    if (!r.creditor?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('personal_auto'),
      id: r.id || uid(),
      creditorName: r.creditor || '',
      estimatedAmount: r.balance || '',
      accountNumber: r.account || '',
      notes: r.notes || '',
    });
  }

  // tax-judgments
  const taxTask = tasks['tax-judgments'] as LegacyTaskSlice | undefined;
  for (const r of taxTask?.taxRows ?? []) {
    if (!r.agency?.trim() && !r.balance?.trim()) continue;
    debts.push({
      ...defaultDebt('tax'),
      id: r.id || uid(),
      creditorName: r.agency || '',
      estimatedAmount: r.balance || '',
      accountNumber: r.caseRef || '',
      notes: r.notes || '',
    });
  }

  return { discoveryChecklist, debts, expenses: [] };
}

// ── Data normalizers (guard missing fields from older saved snapshots) ─────────

function normalizeDebt(d: Record<string, unknown>): DebtItem {
  return {
    id: String(d.id || uid()),
    type: (d.type as DebtType) || 'other',
    creditorName: String(d.creditorName ?? ''),
    estimatedAmount: String(d.estimatedAmount ?? ''),
    accountNumber: String(d.accountNumber ?? ''),
    dueDate: String(d.dueDate ?? ''),
    providerCompany: String(d.providerCompany ?? ''),
    notes: String(d.notes ?? ''),
    status: (d.status as ExecutorStatus) || 'needs_review',
    paymentStatus: (d.paymentStatus as PaymentStatus) || 'unknown',
    actionRequired: (d.actionRequired as ActionRequired) || 'review',
    documentIds: Array.isArray(d.documentIds) ? (d.documentIds as string[]) : [],
  };
}

function normalizeExpense(e: Record<string, unknown>): ExpenseItem {
  return {
    id: String(e.id || uid()),
    type: (e.type as ExpenseType) || 'other',
    expenseName: String(e.expenseName ?? ''),
    estimatedAmount: String(e.estimatedAmount ?? ''),
    frequency: (e.frequency as ExpenseItem['frequency']) || 'monthly',
    accountNumber: String(e.accountNumber ?? ''),
    dueDate: String(e.dueDate ?? ''),
    providerCompany: String(e.providerCompany ?? ''),
    notes: String(e.notes ?? ''),
    status: (e.status as ExecutorStatus) || 'needs_review',
    paymentStatus: (e.paymentStatus as PaymentStatus) || 'unknown',
    actionRequired: (e.actionRequired as ActionRequired) || 'review',
    documentIds: Array.isArray(e.documentIds) ? (e.documentIds as string[]) : [],
  };
}

// ── Style helpers ─────────────────────────────────────────────────────────────

const EXECUTOR_STATUS_LABELS: Record<ExecutorStatus, string> = {
  needs_review: 'Needs Review',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

function statusBadgeClass(s: ExecutorStatus) {
  if (s === 'resolved') return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  if (s === 'in_progress') return 'border-amber-300 bg-amber-50 text-amber-900';
  return 'border-stone-200 bg-stone-100 text-stone-700';
}

const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  current: 'Current',
  past_due: 'Past Due',
  unknown: 'Unknown',
};

const FREQUENCY_LABELS: Record<ExpenseItem['frequency'], string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  unknown: 'Unknown',
};

const SELECT_CLS =
  'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500';
const INPUT_CLS =
  'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500';

// ── Component ─────────────────────────────────────────────────────────────────

export default function SFrame150Liabilities() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId ?? null, {
    readOnly: isArchived,
  });

  const [frameData, setFrameData] = useState<SFrame150FrameData>(defaultFrameData);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [allStepDocs, setAllStepDocs] = useState<SettlementDocumentRow[]>([]);

  // Debt modal
  const [debtModalOpen, setDebtModalOpen] = useState(false);
  const [debtForm, setDebtForm] = useState<DebtItem | null>(null);
  const [isEditingDebt, setIsEditingDebt] = useState(false);

  // Expense modal
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState<ExpenseItem | null>(null);
  const [isEditingExpense, setIsEditingExpense] = useState(false);

  // ── Load ──

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    void (async () => {
      const fd = await loadFrameData(FRAME_ID);
      if (cancelled) return;
      const d = (fd?.data ?? {}) as Record<string, unknown>;

      // New format — has debts or expenses arrays
      if (Array.isArray(d.debts) || Array.isArray(d.expenses)) {
        setFrameData({
          discoveryChecklist: {
            ...defaultDiscovery(),
            ...((d.discoveryChecklist as Record<string, boolean>) ?? {}),
          },
          debts: Array.isArray(d.debts)
            ? (d.debts as Record<string, unknown>[]).map(normalizeDebt)
            : [],
          expenses: Array.isArray(d.expenses)
            ? (d.expenses as Record<string, unknown>[]).map(normalizeExpense)
            : [],
        });
        return;
      }

      // Legacy task-based format (saved as { tasks: Record<TaskId, TaskSlice> })
      if (d.tasks && typeof d.tasks === 'object' && !Array.isArray(d.tasks)) {
        setFrameData(migrateLegacyTasks(d.tasks as Record<string, unknown>));
        return;
      }

      // No recognized data — use defaults (covers very old or empty states)
      setFrameData(defaultFrameData());
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  // ── Documents ──

  const refreshDocs = useCallback(async () => {
    if (!estateId) return;
    const rows = await listSettlementDocuments(estateId, PHASE, STEP_ID);
    setAllStepDocs(rows);
  }, [estateId]);

  useEffect(() => {
    void refreshDocs();
  }, [refreshDocs]);

  const docsByIds = useCallback(
    (ids: string[]) => {
      const wanted = new Set(ids);
      return allStepDocs.filter((d) => wanted.has(d.id)).map(toDocumentUploadRow);
    },
    [allStepDocs]
  );

  // ── Persist ──

  const persistSnapshot = useCallback(
    (next: SFrame150FrameData) => {
      if (!estateId || isArchived) return;
      void saveFrameData(FRAME_ID, next as unknown as Record<string, unknown>);
    },
    [estateId, isArchived, saveFrameData]
  );

  // ── Discovery ──

  const toggleDiscovery = (key: string) => {
    if (isArchived) return;
    setFrameData((prev) => {
      const next: SFrame150FrameData = {
        ...prev,
        discoveryChecklist: { ...prev.discoveryChecklist, [key]: !prev.discoveryChecklist[key] },
      };
      persistSnapshot(next);
      return next;
    });
  };

  // ── Debt CRUD ──

  const openNewDebt = (type: DebtType) => {
    setDebtForm(defaultDebt(type));
    setIsEditingDebt(false);
    setDebtModalOpen(true);
  };

  const openEditDebt = (debt: DebtItem) => {
    setDebtForm({ ...debt });
    setIsEditingDebt(true);
    setDebtModalOpen(true);
  };

  const closeDebtModal = () => {
    setDebtModalOpen(false);
    setDebtForm(null);
    setIsEditingDebt(false);
  };

  const saveDebt = () => {
    if (!debtForm || !debtForm.creditorName.trim()) return;
    setFrameData((prev) => {
      const next: SFrame150FrameData = {
        ...prev,
        debts: isEditingDebt
          ? prev.debts.map((d) => (d.id === debtForm.id ? debtForm : d))
          : [...prev.debts, debtForm],
      };
      persistSnapshot(next);
      return next;
    });
    closeDebtModal();
  };

  const deleteDebt = (id: string) => {
    setFrameData((prev) => {
      const next: SFrame150FrameData = { ...prev, debts: prev.debts.filter((d) => d.id !== id) };
      persistSnapshot(next);
      return next;
    });
  };

  // Debt document upload — debtForm always has a pre-generated id (safe for new items too)
  const handleDebtDocUpload = async (file: File) => {
    if (!debtForm || !estateId || isArchived) return;
    const uploaded = await uploadSettlementDocument({
      estateId,
      phase: PHASE,
      stepId: STEP_ID,
      file,
      description: `debt:${debtForm.id}`,
    });
    await refreshDocs();
    const newDocId = uploaded.id;
    setDebtForm((prev) => prev && { ...prev, documentIds: [...prev.documentIds, newDocId] });
    // If editing an existing debt, persist the updated documentIds immediately
    if (isEditingDebt) {
      setFrameData((prev) => {
        const next: SFrame150FrameData = {
          ...prev,
          debts: prev.debts.map((d) =>
            d.id === debtForm.id ? { ...d, documentIds: [...d.documentIds, newDocId] } : d
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  const handleDebtDocDelete = async (docId: string) => {
    if (!debtForm || !estateId || isArchived) return;
    await deleteSettlementDocument(estateId, docId);
    await refreshDocs();
    setDebtForm((prev) =>
      prev && { ...prev, documentIds: prev.documentIds.filter((x) => x !== docId) }
    );
    if (isEditingDebt) {
      setFrameData((prev) => {
        const next: SFrame150FrameData = {
          ...prev,
          debts: prev.debts.map((d) =>
            d.id === debtForm.id
              ? { ...d, documentIds: d.documentIds.filter((x) => x !== docId) }
              : d
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  // ── Expense CRUD ──

  const openNewExpense = (type: ExpenseType) => {
    setExpenseForm(defaultExpense(type));
    setIsEditingExpense(false);
    setExpenseModalOpen(true);
  };

  const openEditExpense = (expense: ExpenseItem) => {
    setExpenseForm({ ...expense });
    setIsEditingExpense(true);
    setExpenseModalOpen(true);
  };

  const closeExpenseModal = () => {
    setExpenseModalOpen(false);
    setExpenseForm(null);
    setIsEditingExpense(false);
  };

  const saveExpense = () => {
    if (!expenseForm || !expenseForm.expenseName.trim()) return;
    setFrameData((prev) => {
      const next: SFrame150FrameData = {
        ...prev,
        expenses: isEditingExpense
          ? prev.expenses.map((e) => (e.id === expenseForm.id ? expenseForm : e))
          : [...prev.expenses, expenseForm],
      };
      persistSnapshot(next);
      return next;
    });
    closeExpenseModal();
  };

  const deleteExpense = (id: string) => {
    setFrameData((prev) => {
      const next: SFrame150FrameData = {
        ...prev,
        expenses: prev.expenses.filter((e) => e.id !== id),
      };
      persistSnapshot(next);
      return next;
    });
  };

  // Expense document upload
  const handleExpenseDocUpload = async (file: File) => {
    if (!expenseForm || !estateId || isArchived) return;
    const uploaded = await uploadSettlementDocument({
      estateId,
      phase: PHASE,
      stepId: STEP_ID,
      file,
      description: `expense:${expenseForm.id}`,
    });
    await refreshDocs();
    const newDocId = uploaded.id;
    setExpenseForm((prev) => prev && { ...prev, documentIds: [...prev.documentIds, newDocId] });
    if (isEditingExpense) {
      setFrameData((prev) => {
        const next: SFrame150FrameData = {
          ...prev,
          expenses: prev.expenses.map((e) =>
            e.id === expenseForm.id ? { ...e, documentIds: [...e.documentIds, newDocId] } : e
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  const handleExpenseDocDelete = async (docId: string) => {
    if (!expenseForm || !estateId || isArchived) return;
    await deleteSettlementDocument(estateId, docId);
    await refreshDocs();
    setExpenseForm((prev) =>
      prev && { ...prev, documentIds: prev.documentIds.filter((x) => x !== docId) }
    );
    if (isEditingExpense) {
      setFrameData((prev) => {
        const next: SFrame150FrameData = {
          ...prev,
          expenses: prev.expenses.map((e) =>
            e.id === expenseForm.id
              ? { ...e, documentIds: e.documentIds.filter((x) => x !== docId) }
              : e
          ),
        };
        persistSnapshot(next);
        return next;
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      hideNotes
      hideDocuments
    >
      <div className="space-y-10 mb-8">

        {/* ── Discovery section ── */}
        <div className="rounded-xl border border-stone-200 bg-stone-50 overflow-hidden">
          <button
            type="button"
            onClick={() => setDiscoveryOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
          >
            <div>
              <p className="text-sm font-semibold text-blue-900">Find Debts and Liabilities</p>
              <p className="text-xs text-stone-500 mt-0.5">
                Use these steps to identify obligations that may not yet be documented.
              </p>
            </div>
            <svg
              className={`h-4 w-4 text-stone-400 transition-transform shrink-0 ${discoveryOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {discoveryOpen && (
            <div className="px-5 pb-5 space-y-3 border-t border-stone-200 pt-4">
              {DISCOVERY_ITEMS.map((item) => (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-stone-300 text-cyan-600 focus:ring-cyan-500 shrink-0"
                    checked={!!frameData.discoveryChecklist[item.key]}
                    disabled={isArchived}
                    onChange={() => toggleDiscovery(item.key)}
                  />
                  <span className="text-sm text-stone-700">{item.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* ── Section 1: Debts & Liabilities ── */}
        <div>
          <h2 className="text-lg font-semibold text-blue-900 mb-1">Debts & Liabilities</h2>
          <p className="text-sm text-stone-600 mb-5">
            One-time balances to resolve. Select a category tile to add a debt.
          </p>

          {/* Debt tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {DEBT_TILES.map((tile) => {
              const count = frameData.debts.filter((d) => d.type === tile.type).length;
              return (
                <button
                  key={tile.type}
                  type="button"
                  disabled={isArchived}
                  onClick={() => openNewDebt(tile.type)}
                  className="rounded-xl border border-stone-200 bg-white-alt p-4 text-left hover:border-cyan-400 hover:bg-cyan-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 disabled:opacity-50"
                >
                  <p className="text-sm font-semibold text-stone-900">{tile.label}</p>
                  <p className="text-xs text-stone-500 mt-1">
                    {count === 0 ? 'None added' : `${count} added`}
                  </p>
                  <p className="text-xs text-cyan-700 font-medium mt-2">+ Add</p>
                </button>
              );
            })}
          </div>

          {/* Debts list */}
          {frameData.debts.length > 0 ? (
            <div className="space-y-2">
              {frameData.debts.map((debt) => (
                <div
                  key={debt.id}
                  className="rounded-xl border border-stone-200 bg-white-alt p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-stone-900 truncate">
                          {debt.creditorName}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(debt.status)}`}
                        >
                          {EXECUTOR_STATUS_LABELS[debt.status]}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        <span>{DEBT_TYPE_LABELS[debt.type]}</span>
                        {debt.estimatedAmount && <span>Balance: {debt.estimatedAmount}</span>}
                        {debt.dueDate && <span>Due: {debt.dueDate}</span>}
                        <span>Payment: {PAYMENT_STATUS_LABELS[debt.paymentStatus]}</span>
                        {debt.providerCompany && <span>{debt.providerCompany}</span>}
                      </div>
                      {debt.documentIds.length > 0 && (
                        <p className="text-xs text-stone-400 mt-1">
                          {debt.documentIds.length}{' '}
                          {debt.documentIds.length === 1 ? 'document' : 'documents'} attached
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3 shrink-0 mt-0.5">
                      <button
                        type="button"
                        disabled={isArchived}
                        onClick={() => openEditDebt(debt)}
                        className="text-xs text-cyan-700 hover:underline disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={isArchived}
                        onClick={() => deleteDebt(debt.id)}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {debt.notes && (
                    <p className="mt-2 text-xs text-stone-500 italic">{debt.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-400 italic">
              No debts added yet. Use the tiles above to add one.
            </p>
          )}
        </div>

        {/* ── Section 2: Expenses / Recurring ── */}
        <div>
          <h2 className="text-lg font-semibold text-blue-900 mb-1">Expenses / Recurring</h2>
          <p className="text-sm text-stone-600 mb-5">
            Ongoing payments to manage. Select a category tile to add a recurring expense.
          </p>

          {/* Expense tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {EXPENSE_TILES.map((tile) => {
              const count = frameData.expenses.filter((e) => e.type === tile.type).length;
              return (
                <button
                  key={tile.type}
                  type="button"
                  disabled={isArchived}
                  onClick={() => openNewExpense(tile.type)}
                  className="rounded-xl border border-stone-200 bg-white-alt p-4 text-left hover:border-cyan-400 hover:bg-cyan-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 disabled:opacity-50"
                >
                  <p className="text-sm font-semibold text-stone-900">{tile.label}</p>
                  <p className="text-xs text-stone-500 mt-1">
                    {count === 0 ? 'None added' : `${count} added`}
                  </p>
                  <p className="text-xs text-cyan-700 font-medium mt-2">+ Add</p>
                </button>
              );
            })}
          </div>

          {/* Expenses list */}
          {frameData.expenses.length > 0 ? (
            <div className="space-y-2">
              {frameData.expenses.map((expense) => (
                <div
                  key={expense.id}
                  className="rounded-xl border border-stone-200 bg-white-alt p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-stone-900 truncate">
                          {expense.expenseName}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(expense.status)}`}
                        >
                          {EXECUTOR_STATUS_LABELS[expense.status]}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        <span>{EXPENSE_TYPE_LABELS[expense.type]}</span>
                        {expense.estimatedAmount && <span>{expense.estimatedAmount}</span>}
                        <span>{FREQUENCY_LABELS[expense.frequency]}</span>
                        {expense.dueDate && <span>Next due: {expense.dueDate}</span>}
                        {expense.providerCompany && <span>{expense.providerCompany}</span>}
                        <span>Payment: {PAYMENT_STATUS_LABELS[expense.paymentStatus]}</span>
                      </div>
                      {expense.documentIds.length > 0 && (
                        <p className="text-xs text-stone-400 mt-1">
                          {expense.documentIds.length}{' '}
                          {expense.documentIds.length === 1 ? 'document' : 'documents'} attached
                        </p>
                      )}
                    </div>
                    <div className="flex gap-3 shrink-0 mt-0.5">
                      <button
                        type="button"
                        disabled={isArchived}
                        onClick={() => openEditExpense(expense)}
                        className="text-xs text-cyan-700 hover:underline disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={isArchived}
                        onClick={() => deleteExpense(expense.id)}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {expense.notes && (
                    <p className="mt-2 text-xs text-stone-500 italic">{expense.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-400 italic">
              No expenses added yet. Use the tiles above to add one.
            </p>
          )}
        </div>
      </div>

      {/* ── Debt Modal ── */}
      {debtForm && (
        <Modal
          open={debtModalOpen}
          title={isEditingDebt ? 'Edit Debt' : `Add ${DEBT_TYPE_LABELS[debtForm.type]}`}
          okText={isEditingDebt ? 'Save Changes' : 'Add Debt'}
          cancelText="Cancel"
          onOk={saveDebt}
          onCancel={closeDebtModal}
          onClose={closeDebtModal}
          okDisabled={!debtForm.creditorName.trim()}
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Type</label>
              <select
                className={SELECT_CLS}
                value={debtForm.type}
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, type: e.target.value as DebtType })
                }
              >
                {DEBT_TILES.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Creditor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={debtForm.creditorName}
                placeholder="e.g. Chase, IRS, City Hospital"
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, creditorName: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Estimated Balance
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={debtForm.estimatedAmount}
                placeholder="e.g. $5,000"
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, estimatedAmount: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={debtForm.accountNumber}
                placeholder="Optional"
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, accountNumber: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Due Date</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={debtForm.dueDate}
                onChange={(e) => setDebtForm((f) => f && { ...f, dueDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Provider / Company
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={debtForm.providerCompany}
                placeholder="Optional"
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, providerCompany: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
              <textarea
                className={INPUT_CLS}
                rows={3}
                value={debtForm.notes}
                placeholder="Any additional notes..."
                onChange={(e) => setDebtForm((f) => f && { ...f, notes: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Status</label>
              <select
                className={SELECT_CLS}
                value={debtForm.status}
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, status: e.target.value as ExecutorStatus })
                }
              >
                <option value="needs_review">Needs Review</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Payment Status
              </label>
              <select
                className={SELECT_CLS}
                value={debtForm.paymentStatus}
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, paymentStatus: e.target.value as PaymentStatus })
                }
              >
                <option value="current">Current</option>
                <option value="past_due">Past Due</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Action Required
              </label>
              <select
                className={SELECT_CLS}
                value={debtForm.actionRequired}
                onChange={(e) =>
                  setDebtForm((f) => f && { ...f, actionRequired: e.target.value as ActionRequired })
                }
              >
                <option value="pay">Pay</option>
                <option value="continue">Continue</option>
                <option value="cancel">Cancel</option>
                <option value="close">Close</option>
                <option value="review">Review</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Documents</label>
              <DocumentUploadSection
                documents={docsByIds(debtForm.documentIds)}
                disabled={isArchived || !estateId}
                onUpload={handleDebtDocUpload}
                onDelete={!isArchived && estateId ? handleDebtDocDelete : undefined}
                className="[&_h3]:text-xs"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── Expense Modal ── */}
      {expenseForm && (
        <Modal
          open={expenseModalOpen}
          title={
            isEditingExpense
              ? 'Edit Expense'
              : `Add ${EXPENSE_TYPE_LABELS[expenseForm.type]}`
          }
          okText={isEditingExpense ? 'Save Changes' : 'Add Expense'}
          cancelText="Cancel"
          onOk={saveExpense}
          onCancel={closeExpenseModal}
          onClose={closeExpenseModal}
          okDisabled={!expenseForm.expenseName.trim()}
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Type</label>
              <select
                className={SELECT_CLS}
                value={expenseForm.type}
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, type: e.target.value as ExpenseType })
                }
              >
                {EXPENSE_TILES.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Expense Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={expenseForm.expenseName}
                placeholder="e.g. Netflix, Electric Bill, Car Insurance"
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, expenseName: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Estimated Amount
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={expenseForm.estimatedAmount}
                placeholder="e.g. $150"
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, estimatedAmount: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Frequency</label>
              <select
                className={SELECT_CLS}
                value={expenseForm.frequency}
                onChange={(e) =>
                  setExpenseForm((f) => f && {
                    ...f,
                    frequency: e.target.value as ExpenseItem['frequency'],
                  })
                }
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={expenseForm.accountNumber}
                placeholder="Optional"
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, accountNumber: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Next Due Date</label>
              <input
                type="date"
                className={INPUT_CLS}
                value={expenseForm.dueDate}
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, dueDate: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Provider / Company
              </label>
              <input
                type="text"
                className={INPUT_CLS}
                value={expenseForm.providerCompany}
                placeholder="Optional"
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, providerCompany: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
              <textarea
                className={INPUT_CLS}
                rows={3}
                value={expenseForm.notes}
                placeholder="Any additional notes..."
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, notes: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Status</label>
              <select
                className={SELECT_CLS}
                value={expenseForm.status}
                onChange={(e) =>
                  setExpenseForm((f) => f && { ...f, status: e.target.value as ExecutorStatus })
                }
              >
                <option value="needs_review">Needs Review</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Payment Status
              </label>
              <select
                className={SELECT_CLS}
                value={expenseForm.paymentStatus}
                onChange={(e) =>
                  setExpenseForm((f) => f && {
                    ...f,
                    paymentStatus: e.target.value as PaymentStatus,
                  })
                }
              >
                <option value="current">Current</option>
                <option value="past_due">Past Due</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Action Required
              </label>
              <select
                className={SELECT_CLS}
                value={expenseForm.actionRequired}
                onChange={(e) =>
                  setExpenseForm((f) => f && {
                    ...f,
                    actionRequired: e.target.value as ActionRequired,
                  })
                }
              >
                <option value="pay">Pay</option>
                <option value="continue">Continue</option>
                <option value="cancel">Cancel</option>
                <option value="close">Close</option>
                <option value="review">Review</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Documents</label>
              <DocumentUploadSection
                documents={docsByIds(expenseForm.documentIds)}
                disabled={isArchived || !estateId}
                onUpload={handleExpenseDocUpload}
                onDelete={!isArchived && estateId ? handleExpenseDocDelete : undefined}
                className="[&_h3]:text-xs"
              />
            </div>
          </div>
        </Modal>
      )}
    </SettlementStepPage>
  );
}
